import { drawRaster, rasterWidth, rasterHeight, rasterScale } from '@/engine/canvas';
import { makeCanvas, ctxOf } from '@/engine/canvas';

export type Palette = Record<string, string>;

/**
 * Turn ASCII rows into a canvas. Each char is looked up in `pal`; '.' (or missing) is transparent.
 * Rows may be ragged; width is the longest row.
 */
export function sprite(rows: string[], pal: Palette): HTMLCanvasElement {
  const w = Math.max(...rows.map((r) => r.length));
  const h = rows.length;
  const c = makeCanvas(w, h);
  const ctx = ctxOf(c);
  const img = ctx.createImageData(w, h);
  const cache = new Map<string, [number, number, number, number]>();
  for (let y = 0; y < h; y++) {
    const row = rows[y];
    for (let x = 0; x < row.length; x++) {
      const ch = row[x];
      if (ch === '.' || ch === ' ') continue;
      const col = pal[ch];
      if (!col) continue;
      let rgba = cache.get(col);
      if (!rgba) {
        rgba = parseColor(col);
        cache.set(col, rgba);
      }
      const i = (y * w + x) * 4;
      img.data[i] = rgba[0];
      img.data[i + 1] = rgba[1];
      img.data[i + 2] = rgba[2];
      img.data[i + 3] = rgba[3];
    }
  }
  ctx.putImageData(img, 0, 0);
  return c;
}

export function parseColor(col: string): [number, number, number, number] {
  if (col[0] === '#') {
    const hex = col.slice(1);
    if (hex.length === 3) {
      return [parseInt(hex[0] + hex[0], 16), parseInt(hex[1] + hex[1], 16), parseInt(hex[2] + hex[2], 16), 255];
    }
    const r = parseInt(hex.slice(0, 2), 16), g = parseInt(hex.slice(2, 4), 16), b = parseInt(hex.slice(4, 6), 16);
    const a = hex.length >= 8 ? parseInt(hex.slice(6, 8), 16) : 255;
    return [r, g, b, a];
  }
  const m = col.match(/rgba?\(([^)]+)\)/);
  if (m) {
    const p = m[1].split(',').map((s) => parseFloat(s));
    return [p[0], p[1], p[2], p.length > 3 ? Math.round(p[3] * 255) : 255];
  }
  throw new Error('bad color ' + col);
}

export function flipH(src: HTMLCanvasElement): HTMLCanvasElement {
  const c = makeCanvas(rasterWidth(src), rasterHeight(src), rasterScale(src));
  const ctx = ctxOf(c);
  ctx.translate(rasterWidth(src), 0);
  ctx.scale(-1, 1);
  drawRaster(ctx, src, 0, 0);
  return c;
}

/** Compose layers (each optionally offset) onto a new canvas of size w x h. */
export function compose(w: number, h: number, layers: Array<[HTMLCanvasElement, number, number] | null>): HTMLCanvasElement {
  const c = makeCanvas(w, h);
  const ctx = ctxOf(c);
  for (const l of layers) {
    if (!l) continue;
    drawRaster(ctx, l[0], l[1], l[2]);
  }
  return c;
}

/** Tint helper: multiply a sprite by a color (keeps alpha). */
export function tint(src: HTMLCanvasElement, color: string): HTMLCanvasElement {
  const c = makeCanvas(rasterWidth(src), rasterHeight(src), rasterScale(src));
  const ctx = ctxOf(c);
  drawRaster(ctx, src, 0, 0);
  ctx.globalCompositeOperation = 'multiply';
  ctx.fillStyle = color;
  ctx.fillRect(0, 0, c.width, c.height);
  ctx.globalCompositeOperation = 'destination-in';
  drawRaster(ctx, src, 0, 0);
  return c;
}

/** Solid silhouette of a sprite in a color (for outlines/ghosts). */
export function silhouette(src: HTMLCanvasElement, color: string): HTMLCanvasElement {
  const c = makeCanvas(rasterWidth(src), rasterHeight(src), rasterScale(src));
  const ctx = ctxOf(c);
  drawRaster(ctx, src, 0, 0);
  ctx.globalCompositeOperation = 'source-in';
  ctx.fillStyle = color;
  ctx.fillRect(0, 0, c.width, c.height);
  return c;
}

/** Small procedural drawing helper for props/tiles. */
export class Painter {
  readonly c: HTMLCanvasElement;
  readonly ctx: CanvasRenderingContext2D;
  constructor(w: number, h: number, scale = 2) {
    this.c = makeCanvas(w, h, scale);
    this.ctx = ctxOf(this.c);
  }
  rect(x: number, y: number, w: number, h: number, col: string): this {
    this.ctx.fillStyle = col;
    this.ctx.fillRect(x, y, w, h);
    return this;
  }
  px(x: number, y: number, col: string): this {
    return this.rect(x, y, 1, 1, col);
  }
  hline(x: number, y: number, w: number, col: string): this {
    return this.rect(x, y, w, 1, col);
  }
  vline(x: number, y: number, h: number, col: string): this {
    return this.rect(x, y, 1, h, col);
  }
  /** One art-pixel outline, independent of logical world scale. */
  box(x: number, y: number, w: number, h: number, col: string): this {
    const t = 1 / rasterScale(this.c);
    this.rect(x, y, w, t, col).rect(x, y + h - t, w, t, col);
    this.rect(x, y, t, h, col).rect(x + w - t, y, t, h, col);
    return this;
  }
  /** filled box with outline and optional highlight/shade edges */
  panel(x: number, y: number, w: number, h: number, fill: string, outline: string, hi?: string, sh?: string): this {
    this.rect(x, y, w, h, fill);
    if (hi) {
      this.hline(x + 1, y + 1, w - 2, hi);
      this.vline(x + 1, y + 1, h - 2, hi);
    }
    if (sh) {
      this.hline(x + 1, y + h - 2, w - 2, sh);
      this.vline(x + w - 2, y + 1, h - 2, sh);
    }
    this.box(x, y, w, h, outline);
    if (hi && w > 5 && h > 5) this.rect(x + 1, y + 1, w - 2, 0.5, shade(hi, 0.10));
    if (sh && w > 5 && h > 5) this.rect(x + 1, y + h - 1.5, w - 2, 0.5, shade(sh, -0.10));
    return this;
  }
  /** Draw ASCII art at offset. */
  art(x: number, y: number, rows: string[], pal: Palette): this {
    drawRaster(this.ctx, sprite(rows, pal), x, y);
    return this;
  }
  draw(img: HTMLCanvasElement, x: number, y: number): this {
    drawRaster(this.ctx, img, x, y);
    return this;
  }
  /** deterministic speckle noise */
  noise(x: number, y: number, w: number, h: number, col: string, density: number, seed = 1): this {
    let s = seed;
    for (let j = 0; j < h; j++) {
      for (let i = 0; i < w; i++) {
        s = (s * 1103515245 + 12345) & 0x7fffffff;
        if ((s >>> 8) / 8388608 < density) this.px(x + i, y + j, col);
      }
    }
    return this;
  }
  done(): HTMLCanvasElement {
    return this.c;
  }
}

export function shade(hex: string, amt: number): string {
  const [r, g, b] = parseColor(hex);
  const f = (v: number) => Math.max(0, Math.min(255, Math.round(v * (1 + amt))));
  return `rgb(${f(r)},${f(g)},${f(b)})`;
}
